import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { prefetchRoute } from '@/lib/routePrefetch';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { BarChart3, Database, MessageSquare, HelpCircle, GraduationCap, Menu, X, Mail, Settings, LogOut, Home, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUserRole } from '@/hooks/useUserRole';
import { useUserProfile } from '@/hooks/useUserProfile';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';


const navItems = [
  { path: '/dashboards', label: 'Dashboards', icon: BarChart3 },
  { path: '/data-models', label: 'Self-service Analysis', icon: Database },
  { path: '/data-subscriptions', label: 'Data Subscriptions', icon: Mail },
  { path: '/governance', label: 'Governance', icon: Shield },
  { path: '/chat', label: 'Chat with Data', icon: MessageSquare },
  { path: '/support', label: 'Support', icon: HelpCircle },
  { path: '/learn', label: 'Learn', icon: GraduationCap },
  // Management page removed - functionality integrated into individual pages
];

export const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { hasRole, isAdmin } = useUserRole();
  const isMobile = useIsMobile();
  const { data: profile } = useUserProfile(user?.id);

  // All navigation items are now publicly accessible
  const filteredNavItems = navItems;

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white">
      {/* Top black line */}
      <div className="h-[7px] bg-black w-full"></div>
      
      {/* Logo and user controls section */}
      <div className="flex items-center justify-between px-6 py-4">
        {/* Logo - centered */}
        <div className="flex-1 flex justify-center">
          <NavLink to="/">
            <img 
              src="/lovable-uploads/745e2638-f665-45e7-982a-a92789aeebaf.png" 
              alt="my DATA PORTAL" 
              className="h-12 object-contain"
            />
          </NavLink>
        </div>
        
        {/* User controls - right aligned */}
        <div className="flex items-center space-x-3 ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-10 w-10 p-0 rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={profile?.avatar_url || ""} alt={profile?.display_name || "User"} />
                  <AvatarFallback className="bg-gray-600 text-white text-sm">
                    {profile?.display_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {isMobile && (
            <Button
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          )}
        </div>
      </div>

      {/* Navigation menu */}
      <div className="px-6">
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center justify-center space-x-8 relative">
          {/* Home icon only */}
          <NavLink
            to="/"
            className={({ isActive }) =>
              `py-3 transition-all duration-200 relative group ${
                isActive ? 'text-black' : 'text-gray-600 hover:text-black'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="currentColor"></path>
                </svg>
                {/* Black underline for active/hover state */}
                <div className={`absolute bottom-0 left-0 right-0 h-[2px] bg-black transition-opacity duration-200 ${
                  isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`} />
              </>
            )}
          </NavLink>
          
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onMouseEnter={() => prefetchRoute(item.path)}
              className={({ isActive }) =>
                `py-3 transition-all duration-200 relative group ${
                  isActive ? 'text-black' : 'text-gray-600 hover:text-black'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="font-semibold uppercase text-sm tracking-wide font-sans">
                    {item.label}
                  </span>
                  {/* Black underline for active/hover state */}
                  <div className={`absolute bottom-0 left-0 right-0 h-[2px] bg-black transition-opacity duration-200 ${
                    isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`} />
                </>
              )}
            </NavLink>
          ))}
          
          {/* Admin Navigation Item */}
          {isAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `py-3 transition-all duration-200 relative group ${
                  isActive ? 'text-black' : 'text-gray-600 hover:text-black'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="font-semibold uppercase text-sm tracking-wide font-sans">
                    ADMIN
                  </span>
                  {/* Black underline for active/hover state */}
                  <div className={`absolute bottom-0 left-0 right-0 h-[2px] bg-black transition-opacity duration-200 ${
                    isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`} />
                </>
              )}
            </NavLink>
          )}
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="py-3 space-y-2 border-t border-gray-200">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `block py-2 transition-all duration-200 ${
                    isActive ? 'text-black' : 'text-gray-600'
                  }`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="currentColor"></path>
                </svg>
              </NavLink>
              
              {filteredNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onMouseEnter={() => prefetchRoute(item.path)}
                  className={({ isActive }) =>
                    `block py-2 transition-all duration-200 ${
                      isActive ? 'text-black' : 'text-gray-600'
                    }`
                  }
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="font-semibold uppercase text-sm tracking-wide font-sans">
                    {item.label}
                  </span>
                </NavLink>
              ))}
              
              {/* Mobile Admin Navigation */}
              {isAdmin && (
                <NavLink
                  to="/admin"
                  className={({ isActive }) =>
                    `block py-2 transition-all duration-200 ${
                      isActive ? 'text-black' : 'text-gray-600'
                    }`
                  }
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="font-semibold uppercase text-sm tracking-wide font-sans">
                    ADMIN
                  </span>
                </NavLink>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Bottom grey line */}
      <div className="h-[1px] bg-gray-300 w-full"></div>
    </div>
  );
};